import { addListener, type AnyAction, createListenerMiddleware, isAnyOf, type TypedAddListener, type TypedStartListening } from '@reduxjs/toolkit';
import { setContext } from '@alltrails/context/slices/contextSlice';
import { setListsAndListItems } from '@alltrails/lists/slices/lists';
import type ListItem from '@alltrails/lists/types/ListItem';
import getListsAndListItems from '@alltrails/lists/utils/requests';
import { clearViewingWaypointId, setViewingWaypointId } from '@alltrails/maps/redux/customRoute';
import {
  clearSelectedWaypoint,
  setSelectedWaypoint,
  updateNonTrailListResults,
  updateSelectedPOI,
  updateTrailResults
} from '@alltrails/maps/redux/reducer';
import type { NonTrailListResult } from '@alltrails/maps/redux/reducer';
import type { TrailSearchResult } from '@alltrails/types/AlgoliaResults';
import { writeStoredAdvancedMapping } from '@/components/AdvancedMapping/advancedMappingStorage';
import type { AppDispatch, RootState } from './redux';
import { clearElevationChartZoom, setExcludedPoiGroups, toggleContentVisibility, toggleGradeShading } from './slices/advancedMappingSlice';
import { setListPageItems, upsertListPageItem } from './slices/exploreSlice';

export const listenerMiddleware = createListenerMiddleware();

export type AppStartListening = TypedStartListening<RootState, AppDispatch>;

export const startAppListening = listenerMiddleware.startListening as AppStartListening;

export const addAppListener = addListener as TypedAddListener<RootState, AppDispatch>;

const onUserLogin = async (action, listenerApi) => {
  if (action.payload.currentUser) {
    const userLists = await getListsAndListItems();
    listenerApi.dispatch(setListsAndListItems(userLists)); // dispatch lists info
  }
};

const onListMapItemsChange = ({ payload: listItems }: { payload: ListItem[] }, { dispatch }) => {
  const trailResults = listItems
    .filter(item => item.type === 'trail' && item.object?._geoloc)
    .map(item => ({ ...item.object, ID: item.objectId }) as TrailSearchResult);
  dispatch(updateTrailResults(trailResults));

  const nonTrailResults = listItems.flatMap<NonTrailListResult>(item => {
    if (item.type !== 'map' && item.type !== 'track') {
      return [];
    }
    const object = item.object;
    if (!object?._geoloc) {
      return [];
    }
    return [
      {
        ID: item.objectId,
        _geoloc: object._geoloc,
        name: object.name,
        slug: object.slug,
        type: item.type,
        meters: object.meters ?? object.length,
        durationMinutes: object.duration_minutes ?? object.minutes,
        elevationGain: item.type === 'track' ? item.object?.elevation_gain : undefined,
        activityType: item.type === 'track' ? item.object?.activity_type : undefined,
        createdAt: item.metadata?.created
      }
    ];
  });
  dispatch(updateNonTrailListResults(nonTrailResults.length ? nonTrailResults : undefined));
};

const onLatestListActionChange = (_action, { getState, dispatch }) => {
  const state = getState() as RootState;
  const latestListAction = state.lists.latestListAction;
  if (!latestListAction) {
    return;
  }

  if (!state.explore.listPageInfo) {
    return;
  }

  const { listItem, action } = latestListAction;

  if (action === 'remove') {
    if (listItem.type === 'trail') {
      const remaining = Object.values(state.map.trailResults ?? {})
        .flat()
        .filter(t => t.ID !== listItem.objectId);
      dispatch(updateTrailResults(remaining));
    } else {
      const remaining = (state.map.nonTrailListResults ?? []).filter(r => r.ID !== listItem.objectId);
      dispatch(updateNonTrailListResults(remaining.length ? remaining : undefined));
    }
  } else if (action === 'add') {
    if (listItem.object) {
      const now = new Date().toISOString();
      dispatch(
        upsertListPageItem({
          ...listItem,
          metadata: {
            created: listItem.metadata?.created ?? now,
            updated: listItem.metadata?.updated ?? now,
            status: listItem.metadata?.status ?? 'active'
          }
        })
      );
    }
  }
  if (action === 'add' && listItem.object?._geoloc) {
    if (listItem.type === 'trail') {
      const current = Object.values(state.map.trailResults ?? {}).flat();
      if (!current.some(t => t.ID === listItem.objectId)) {
        dispatch(updateTrailResults([...current, { ...listItem.object, ID: listItem.objectId } as unknown as TrailSearchResult]));
      }
    } else {
      const object = listItem.object;
      if (object && '_geoloc' in object) {
        const current = state.map.nonTrailListResults ?? [];
        if (!current.some(r => r.ID === listItem.objectId)) {
          dispatch(
            updateNonTrailListResults([
              ...current,
              {
                ID: listItem.objectId,
                _geoloc: object._geoloc,
                name: object.name,
                slug: object.slug,
                type: listItem.type,
                meters: 'meters' in object ? object.meters : undefined,
                durationMinutes: 'duration_minutes' in object ? object.duration_minutes : undefined
              } as NonTrailListResult
            ])
          );
        }
      }
    }
  }
};

/**
 * Mutual exclusion between Redux fields that "claim" the map sidebar.
 *
 * The explore app currently has several independent Redux fields that each cause the sidebar to switch
 * to a different view (`selectedWaypoint`, `selectedPOI`, `viewingWaypointId`, `activeSegmentIndex`).
 *
 * This listener enforces the invariant that only one such "claim" is active at a time. When any one fires,
 * the others are cleared. If a new sidebar-claim is added later, be sure to add it to BOTH the `predicate` (so the
 * listener fires on it) and the `effect` body (so other claims clear it).
 *
 * Notes:
 *   - `isEditingWaypoint` is intentionally NOT in this listener. While a route-waypoint edit is open,
 *     other claim sites (e.g. UserWaypoints click handler) gate their own dispatches client-side so the
 *     user can't accidentally drop unsaved edits.
 *   - Longer-term: consolidate these fields into a single tagged-union `mapSidebarOwner` on the map slice;
 *     this listener is the migration bridge.
 */
const isSidebarClaimAction = (action: AnyAction): boolean => {
  if (setSelectedWaypoint.match(action)) {
    return true;
  }
  if (setViewingWaypointId.match(action)) {
    return true;
  }
  if (updateSelectedPOI.match(action) && action.payload !== undefined) {
    return true;
  }
  return false;
};

const onSidebarClaim: Parameters<AppStartListening>[0]['effect'] = (action, { dispatch, getState }) => {
  const state = getState();
  if (!setSelectedWaypoint.match(action) && state.map.selectedWaypoint !== undefined) {
    dispatch(clearSelectedWaypoint());
  }
  if (!updateSelectedPOI.match(action) && state.map.selectedPOI !== undefined) {
    dispatch(updateSelectedPOI(undefined));
  }
  if (!setViewingWaypointId.match(action) && state.customRoute.viewingWaypointId !== undefined) {
    dispatch(clearViewingWaypointId());
  }
};

export const setupListeners = () => {
  startAppListening({ actionCreator: setContext, effect: onUserLogin });
  startAppListening({ actionCreator: setListPageItems, effect: onListMapItemsChange });
  startAppListening({
    predicate: (_action, currentState, previousState) =>
      (currentState as RootState).lists.latestListAction !== (previousState as RootState).lists.latestListAction,
    effect: onLatestListActionChange
  });
  startAppListening({ predicate: isSidebarClaimAction, effect: onSidebarClaim });
  startAppListening({
    matcher: isAnyOf(setExcludedPoiGroups, toggleContentVisibility, toggleGradeShading),
    effect: (_action, { getState }) => {
      writeStoredAdvancedMapping(getState().advancedMapping);
    }
  });
  // A chart zoom is a range of route distances dragged against one selection, so it is dropped when either moves: the
  // selection, or the segments the distances are measured along (an edit, undo, a reorder, or the metadata that lands
  // after one and reassigns every distance). Clearing on the change itself covers every action that makes it without
  // listing them.
  startAppListening({
    predicate: (_action, currentState, previousState) => {
      const current = currentState as RootState;
      const previous = previousState as RootState;
      return (
        current.advancedMapping.elevationChartZoom !== undefined &&
        (current.customRoute.activeSegmentIndex !== previous.customRoute.activeSegmentIndex ||
          current.customRoute.route.segments !== previous.customRoute.route.segments)
      );
    },
    effect: (_action, { dispatch }) => {
      dispatch(clearElevationChartZoom());
    }
  });
  // DataDome auto-refreshes the page once a captcha is passed. While a user is building or editing a
  // custom route that refresh would discard their unsaved work, so we opt out of it whenever editing
  // is active and restore the default behavior once editing ends.
  startAppListening({
    predicate: (_action, currentState, previousState) =>
      (currentState as RootState).customRoute.isEditing !== (previousState as RootState).customRoute.isEditing,
    effect: (_action, { getState }) => {
      if (typeof window === 'undefined') {
        return;
      }

      window.ddoptions = window.ddoptions ?? {};
      window.ddoptions.disableAutoRefreshOnCaptchaPassed = !!getState().customRoute.isEditing;
    }
  });
};
